/* =====================================================================
 * LiveAgentPage — 라이브 에이전트 메인 페이지
 *
 * 통합 에이전트(AgentContent) 베이스 + 6개 예외 적용:
 *  1) Dock 바 (인풋 위)
 *  2) 슬랙/디스코드 제외
 *  3) + New live session
 *  4) 시작하기 버튼 (예시 카드 대신)
 *  5) 그룹 채팅 제외
 *  6) Fast 모드 고정 (드롭다운 없음)
 * ===================================================================== */

import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  useLayoutEffect,
  useMemo,
} from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Radio, Copy, Check, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  useLiveAgentStore,
  type LiveAgentMessage,
  type LiveAgentName,
  onAppEvent,
} from '@auw/stores';
import { useChatStore } from '@auw/stores';
import { loadMessages } from '@auw/api-client';
import type { ChatMessage } from '@auw/workspace-utils';
import { useLiveAgentSession } from './useLiveAgentSession';
import { prefetchLivePlugins } from './livePluginsPrefetch';
import { EmptyState } from '@auw/workspace-ui';
import { Tooltip } from '@auw/workspace-ui';
import { LiveAgentSettingsModal } from './LiveAgentSettingsModal';
import { LiveAgentCardRail } from './LiveAgentCardRail';
import { useFeature } from '@auw/feature-flags';

/* ------------------------------------------------------------------ */
/* User-message anchoring constants — `AgentContent.tsx` 와 1:1 동일.   */
/* ------------------------------------------------------------------ */

/** 컨테이너 하단에서 이 거리(px) 이내면 "맨 아래에 있다" 로 간주. */
const NEAR_BOTTOM_PX = 120;
/** 앵커링된 user 버블이 viewport 상단으로부터 떨어진 거리(px). */
const USER_ANCHOR_TOP_PX = 12;
/** 앵커링 활성 시 메시지 영역 끝에 추가하는 빈 spacer 높이.
 *
 *  반드시 컨테이너 viewport 1개분(`100vh`) 이상이어야 한다.  spacer 가
 *  부족하면 `desired_top = userRow.top - 12` 가 컨테이너의 `max scrollTop`
 *  (= scrollHeight - clientHeight) 을 초과해 브라우저가 자동 clamp 하고,
 *  그 결과 user 버블이 viewport 상단까지 올라가지 못한 채 "절반만 점프"
 *  하는 현상이 발생한다.  라이브 에이전트는 partial transcript 첫 chunk
 *  가 1-3글자로 매우 짧게 도착하므로 user bubble height 가 짧아 이
 *  clamp 가 더 자주 발생한다 — 그래서 통합 에이전트와 1:1 동일한 정책
 *  으로 100vh 를 사용한다. */
const USER_ANCHOR_BOTTOM_SPACER = '100vh';
/** programmatic 스크롤(`alignAnchoredUser`/`scrollToBottom`) 직후 이
 *  시간(ms) 동안의 `scroll` 이벤트는 사용자 발의 스크롤로 보지 않음. */
const PROGRAMMATIC_SCROLL_GUARD_MS = 120;

function nowMs() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/**
 * 사이드바의 라이브 에이전트 최근 기록 항목을 클릭하면 chatStore의 해당 conversation이
 * active가 된다. 그 conversation에 저장돼 있는 `ChatMessage` 히스토리를 LiveAgent UI가
 * 쓰는 `LiveAgentMessage` 포맷으로 변환한다.
 */
function chatMessageToLiveAgentMessage(m: ChatMessage): LiveAgentMessage {
  if (m.role === 'user') {
    return {
      id: m.id,
      author: 'user',
      text: m.content,
      timestamp: m.createdAt,
      transcript: m.content,
      persisted: true,
    };
  }
  // agent_name 복원 — 백엔드 loadMessages 는 metadata 를 복원하지 않고
  // completedData 만 round-trip 하므로, completedData.agent_name 을 우선
  // 보고 metadata.agent_name 으로 폴백한다(메모리/캐시 경로 대비).
  const completed = m.completedData as { agent_name?: unknown } | undefined;
  const fromCompleted = typeof completed?.agent_name === 'string' ? completed.agent_name : '';
  const fromMeta = typeof m.metadata?.agent_name === 'string' ? m.metadata.agent_name : '';
  const agentName = (fromCompleted || fromMeta || 'Rebo') as LiveAgentName;
  return {
    id: m.id,
    author: agentName,
    text: m.content,
    timestamp: m.createdAt,
    transcript: m.content,
    persisted: true,
  };
}

const AGENT_BRAND_SUFFIXES = [
  '.team',
  '.agent',
  '.mate',
  '.rebo',
];

/* ------------------------------------------------------------------ */
/* Animations                                                          */
/* ------------------------------------------------------------------ */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ------------------------------------------------------------------ */
/* Styled — 통합 에이전트 UI 통일                                        */
/* ------------------------------------------------------------------ */

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  position: relative;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const MessagesInner = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

/* `AgentContent` 와 동일한 정책으로 — 앵커링 활성 시에만 70vh spacer 를
 * 추가한다. inline style 로 조건부 렌더하므로 별도 styled 가 필요 없다.
 * (이전엔 항상 60vh `EndSpacer` 를 두었지만 통합 에이전트 정책에 맞춰
 * 제거하고 inline `<div style={{ minHeight: USER_ANCHOR_BOTTOM_SPACER }}/>`
 * 로 일원화함.) */

/* Row: 통합 에이전트의 MessageBubble Row와 동일 */
const Row = styled.div<{ $isUser: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.bubble?.rowGap ?? '12px'};
  max-width: ${({ theme }) => theme.sizes?.maxMessageWidth ?? '768px'};
  width: 100%;
  margin: 0 auto;
  padding: ${({ theme }) => theme.bubble?.rowPad ?? '8px 16px'};
  animation: ${fadeIn} 0.3s ease;

  ${({ $isUser }) =>
    $isUser &&
    css`
      justify-content: flex-end;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints?.mobile ?? '768px'}) {
    gap: ${({ theme }) => theme.bubbleMobile?.rowGap ?? '8px'};
    padding: ${({ theme }) => theme.bubbleMobile?.rowPad ?? '6px 12px'};
  }
`;

/* 아바타 열 */
const AvatarCol = styled.div`
  margin-top: 2px;
  flex-shrink: 0;
`;

const AgentAvatarImg = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 22.37%;
  object-fit: contain;
  display: block;
`;

/* 에이전트 메시지: AssistantWallpaperCard 스타일 (글래스모피즘) */
const AssistantCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  margin-top: 2px;
  padding: 14px 16px 12px;
  border-radius: 22px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.52)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(18,18,22,0.42)' : 'rgba(255,255,255,0.44)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 16px 36px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.06)'
      : '0 14px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.38)'};
  backdrop-filter: blur(20px) saturate(155%);
  -webkit-backdrop-filter: blur(20px) saturate(155%);
`;

const AssistantContent = styled.div`
  font-size: ${({ theme }) => theme.bubble?.assistantFontSize ?? '0.9375rem'};
  line-height: ${({ theme }) => theme.bubble?.assistantLineHeight ?? '1.7'};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: pre-wrap;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints?.mobile ?? '768px'}) {
    font-size: ${({ theme }) => theme.bubbleMobile?.assistantFontSize ?? '0.875rem'};
    line-height: ${({ theme }) => theme.bubbleMobile?.assistantLineHeight ?? '1.65'};
  }
`;

const AgentNameLabel = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: #E06B50;
  margin-bottom: 4px;
`;

const BubbleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
`;

const ActionBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'};
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
    color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)'};
  }
`;

/* ---- Assistant 카드 내부 액션 영역 (통합 에이전트 MessageBubble과 동일 UX) ---- */

const AssistantActions = styled.div`
  display: flex;
  gap: 2px;
  margin-top: 6px;
`;

const CardActionBtn = styled.button`
  padding: ${({ theme }) => theme.bubble?.actionBtnPad ?? '5px'};
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(58, 58, 58, 0.86)'};
  transition: background ${({ theme }) => theme.transitions?.fast ?? '0.15s'},
    color ${({ theme }) => theme.transitions?.fast ?? '0.15s'};
  display: flex;
  align-items: center;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints?.mobile ?? '768px'}) {
    padding: ${({ theme }) => theme.bubbleMobile?.actionBtnPad ?? '4px'};
  }
`;

const thumbShake = keyframes`
  0%   { transform: rotate(0deg); }
  20%  { transform: rotate(-18deg); }
  40%  { transform: rotate(14deg); }
  60%  { transform: rotate(-8deg); }
  80%  { transform: rotate(4deg); }
  100% { transform: rotate(0deg); }
`;

const CardFeedbackBtn = styled(CardActionBtn)<{ $active: boolean }>`
  & > svg {
    fill: ${({ $active }) => ($active ? 'currentColor' : 'none')};
    transition: fill 0.15s ease;
  }
  &.shaking > svg {
    animation: ${thumbShake} 0.45s ease-in-out;
  }
`;

const UserSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: ${({ theme }) => theme.bubble?.userMaxWidth ?? '75%'};

  &:hover ${BubbleActions} {
    opacity: 1;
  }
`;

const UserBubble = styled.div`
  position: relative;
  background: ${({ theme }) => theme.colors?.bgUserMessage ?? (theme.mode === 'dark' ? '#2f2f2f' : '#f7f7f8')};
  border-radius: 16px 16px 4px 16px;
  padding: ${({ theme }) => theme.bubble?.userPad ?? '10px 20px'};
  width: fit-content;
  max-width: 100%;
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.transitions?.fast ?? '0.15s'};
  font-size: ${({ theme }) => theme.bubble?.assistantFontSize ?? '0.9375rem'};
  line-height: ${({ theme }) => theme.bubble?.assistantLineHeight ?? '1.7'};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: pre-wrap;
  word-break: break-word;

  &:hover {
    opacity: 0.85;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints?.mobile ?? '768px'}) {
    padding: ${({ theme }) => theme.bubbleMobile?.userPad ?? '8px 16px'};
  }
`;

/* BubbleRow 는 ``Row`` 의 두 번째 flex item 이므로 ``min-width: 0`` 을 명시
 * 해 자식(``LiveAgentCardRail`` 의 wide content) 이 부모 max-width(=
 * ``theme.sizes.maxMessageWidth``) 를 밀어내지 못하게 한다. flex item 의
 * 기본값인 ``min-width: auto`` 는 content size 미만으로 못 줄어들기 때문에
 * 카드가 여러 장이면 BubbleRow 가 채팅창을 가로로 넘어 늘어났다.  ``flex:
 * 1 1 auto`` 로 가용 공간을 채우고, 내부 카드 레일은 자체적인 가로 스크롤
 * 로 overflow 를 처리한다. */
const BubbleRow = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;

  &:hover ${BubbleActions} {
    opacity: 1;
  }
`;

/* ---- Start page ---- */

const StartCard = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 32px;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  animation: ${fadeIn} 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  transition: all 0.3s ease;
  max-width: 360px;
  width: 100%;

  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.06)'
      : 'rgba(0, 0, 0, 0.04)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.06)'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(224, 107, 80, 0.15);
    border-color: rgba(224, 107, 80, 0.3);
  }
`;

const StartIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(224, 107, 80, 0.15);
  color: #E06B50;
`;

/* 라이브 세션 상태 표시는 `LiveAgentDock`의 StatusChip + ambient overlay +
 * 전역 Toast(`uiStore`)로 이관되었다. 기존 상단 StatusBar는 앱 디자인 언어
 * (글래스/브랜드 컬러) 와 이질적이고 정보 중복이 커서 제거한다. */

/* ------------------------------------------------------------------ */
/* Thinking indicator                                                  */
/*                                                                     */
/* 라이브 에이전트는 정제된 finished transcript 가 도착해야만 어시스턴트
 * 메시지를 store 에 push 한다 (`useLiveAgentSession.ts` partial 처리 정책
 * 참조).  그 사이 오디오는 이미 흘러나오므로 user 발화 종료 ~ 어시스턴트
 * 버블 등장 사이에 "음성은 들리는데 화면에 빈 공간"인 구간이 생긴다.
 *
 * 이 구간을 시각적으로 채우기 위해 *render-only sentinel* 로 dot pulsing
 * indicator 를 마지막 메시지가 user 일 때만 mount 한다.  store 는 절대
 * 건드리지 않으므로 lastUserId / 앵커링 / 메시지 카운트 기반 effect 가
 * 영향을 받지 않는다.
 *
 * 색은 `theme.colors.brand` 로만 잡아 b2c (askyourai 주황) / enterprise
 * (samsung 파랑) tenant 가 자동 반영된다.
 */
const dotPulse = keyframes`
  0%, 60%, 100% { opacity: 0.25; transform: scale(0.85); }
  30%           { opacity: 1;    transform: scale(1); }
`;

const TypingDots = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 2px;
`;

const Dot = styled.span<{ $delay: number }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.brand};
  animation: ${dotPulse} 1.2s ease-in-out infinite;
  animation-delay: ${({ $delay }) => `${$delay}s`};
`;

/** 어시스턴트 자리에 placeholder 버블 1개 — 메시지처럼 보이도록 ``Row`` /
 *  ``AvatarCol`` / ``AssistantCard`` 골격을 그대로 재사용한다. */
const TypingIndicator: React.FC<{ ariaLabel: string }> = ({ ariaLabel }) => (
  <Row $isUser={false} aria-live="polite" aria-label={ariaLabel}>
    <AvatarCol>
      <AgentAvatarImg src="/agent_logo.png" alt="" />
    </AvatarCol>
    <BubbleRow>
      <AssistantCard>
        <TypingDots role="status">
          <Dot $delay={0} />
          <Dot $delay={0.18} />
          <Dot $delay={0.36} />
        </TypingDots>
      </AssistantCard>
    </BubbleRow>
  </Row>
);

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

async function copyTextFallback(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try { await navigator.clipboard.writeText(text); return true; } catch { /* fallthrough */ }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

export const LiveAgentPage: React.FC = () => {
  const { t } = useTranslation('common');
  const micActiveRef = useRef(false);
  const [copiedIds, setCopiedIds] = useState<Set<string>>(new Set());
  const [feedbackMap, setFeedbackMap] = useState<Record<string, 'up' | 'down'>>({});
  const [shakingFeedback, setShakingFeedback] = useState<{ id: string; type: 'up' | 'down' } | null>(null);

  /* ------------------------------------------------------------------ */
  /* User-message anchoring — `AgentContent.tsx` 정책과 1:1 동일.        */
  /*                                                                    */
  /* 새 user 발화가 등장하면 해당 user 버블을 viewport 상단(`USER_ANCHOR_TOP_PX`)
   * 으로 즉시(`behavior: 'auto'`) 점프시키고, 이후 transcript 가 점진적으로
   * 스트리밍되며 layout 이 자라는 동안 `ResizeObserver` 가 같은 위치를
   * 유지하도록 재정렬한다. 사용자가 wheel/touch 로 직접 스크롤하면 앵커는
   * 즉시 해제된다.                                                       */
  /* ------------------------------------------------------------------ */
  const containerRef = useRef<HTMLDivElement | null>(null);
  /** 컨테이너가 현재 "맨 아래에 가까운가" — 통합 에이전트(`AgentContent`)
   *  는 이 값을 `agent:scrollState` 이벤트로 emit 해 외부 ScrollToBottom
   *  버튼을 토글하지만, 라이브 에이전트는 그 버튼이 없으므로 ref 만으로도
   *  충분하다. (향후 공통 hook 으로 추출할 때 state 로 승격할 수 있다.) */
  const isNearBottomRef = useRef(true);
  /** 현재 user 버블을 상단에 앵커링하고 있는지 여부 — 활성 시
   *  `USER_ANCHOR_BOTTOM_SPACER` 만큼의 빈 공간을 메시지 영역 끝에 둔다. */
  const [isAnchoringUser, setIsAnchoringUser] = useState(false);
  /** 앵커링 활성 플래그(ref). state 와 1:1 동기지만, 콜백 의존 배열에서
   *  ref 를 읽으면 stale-closure 를 피할 수 있다. */
  const anchorActiveRef = useRef(false);
  /** layout 이 아직 안정되지 않았을 때 들어온 anchor 요청을 보류해 두는
   *  플래그.  resize observer 가 다음 layout 사이클에서 이를 처리한다. */
  const pendingAnchorRequestRef = useRef(false);
  /** 보류 중인 anchor 요청 시점의 latestUserId — 그 사이에 latestUserId
   *  가 또 바뀌면 보류 요청은 무효화된다. */
  const pendingAnchorBaselineUserIdRef = useRef<string | null>(null);
  /** 현재 앵커링 대상 user message id. */
  const anchorMessageIdRef = useRef<string | null>(null);
  /** 가장 최근 programmatic 스크롤 시각 — 이 시점부터
   *  `PROGRAMMATIC_SCROLL_GUARD_MS` 동안의 `scroll` 이벤트는 사용자
   *  발의 스크롤로 보지 않는다. */
  const lastProgrammaticScrollAtRef = useRef(0);
  /** conversation 전환 시 1회만 맨 아래로 점프하기 위한 가드. */
  const initialScrollConversationRef = useRef<string | null>(null);

  const messages = useLiveAgentStore((s) => s.messages);
  const sessionStatus = useLiveAgentStore((s) => s.sessionStatus);
  /** 마지막 메시지가 user 이면 어시스턴트 응답을 기다리는 중. transcript
   *  로 finished 가 도착해야 어시스턴트 버블이 등장하므로 그 사이 dot
   *  pulsing indicator 로 공백을 채운다.  250ms 지연을 둬서 매우 짧은
   *  응답에서도 indicator 가 깜빡이지 않게 한다. */
  const [showThinkingIndicator, setShowThinkingIndicator] = useState(false);
  /* P11-5i: when `mode.live-agent.transcription` is disabled the
   * transcription bubble surface (text content + copy / feedback CTAs)
   * is hidden — the audio session itself still runs through
   * `useLiveAgentSession`, but no transcript text is displayed. */
  const transcriptionEnabled = useFeature('mode.live-agent.transcription');

  /*
   * messages 중 가장 마지막 user 메시지 id. 새 id 가 등장하면 그 메시지로
   * 스크롤한다. id 는 생성 시점에 한 번 할당되고 이후에도 안 바뀌므로,
   * transcript 누적 업데이트에서 불필요한 재스크롤이 일어나지 않는다.
   */
  const lastUserId = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].author === 'user') return messages[i].id;
    }
    return null;
  }, [messages]);

  /* ---- 최근 기록(활성 conversation) 동기화 ----
   *
   * 라이브 에이전트는 chatStore와 분리된 `liveAgentStore`에서 메시지를 관리한다.
   * 따라서 사이드바에서 과거 대화를 클릭해도 activeId만 바뀌고 화면 메시지는
   * 갱신되지 않아 시작 페이지(EmptyState)가 보이는 문제가 있었다.
   * 여기서 chatStore의 활성 conversation 메시지를 LiveAgentMessage로 변환해
   * liveAgentStore에 반영한다. 진행 중인 세션이 있다면 덮어쓰지 않는다.
   */
  const activeId = useChatStore((s) => s.activeId);
  const activeConversation = useChatStore((s) =>
    s.activeId ? s.conversations[s.activeId] ?? null : null,
  );
  const activeConversationMessages = activeConversation?.messages;

  useEffect(() => {
    const liveState = useLiveAgentStore.getState();
    const conv = activeConversation;

    if (!conv || conv.mode !== 'live-agent') {
      if (
        (liveState.conversationId || liveState.messages.length > 0)
        && (liveState.sessionStatus === 'idle' || liveState.sessionStatus === 'disconnected')
      ) {
        liveState.clearSession();
      }
      return;
    }

    const isDifferentConversation = liveState.conversationId !== conv.id;
    if (isDifferentConversation) {
      if (liveState.sessionStatus === 'connecting' || liveState.sessionStatus === 'active') {
        liveState.stopSessionFn?.();
      }
      liveState.clearSession();
      liveState.setConversationId(conv.id);

      // chatStore 가 들고 있는 이력으로 우선 즉시 렌더(있으면). 라이브 대화는
      // 세션 시작 시 item 을 선생성하므로 chatStore.messages 가 비어 있는
      // 경우가 많다 — 그래서 백엔드에서 권위 있는 메시지를 비동기로 로드해
      // 덮어쓴다. 이게 "재클릭 시 시작페이지(컨텍스트 유실)" 의 근본 해결.
      const seed = (conv.messages ?? []).map(chatMessageToLiveAgentMessage);
      if (seed.length > 0) liveState.setMessages(seed);

      const targetId = conv.id;
      void loadMessages(targetId)
        .then((msgs) => {
          if (msgs.length === 0) return;
          const s = useLiveAgentStore.getState();
          // 그 사이 다른 대화로 전환했거나 세션이 시작됐으면 무시(레이스 가드).
          if (s.conversationId !== targetId) return;
          if (s.sessionStatus === 'active' || s.sessionStatus === 'connecting') return;
          s.setMessages(msgs.map(chatMessageToLiveAgentMessage));
        })
        .catch(() => {});
      return;
    }

    // 같은 conversation이지만 이력이 비동기로 뒤늦게 도착한 경우 한 번 더 반영한다.
    if (
      (liveState.sessionStatus === 'idle' || liveState.sessionStatus === 'disconnected')
      && (conv.messages?.length ?? 0) > liveState.messages.length
    ) {
      liveState.setMessages((conv.messages ?? []).map(chatMessageToLiveAgentMessage));
    }
  }, [activeId, activeConversation, activeConversationMessages]);

  const {
    startSession,
    startMicrophone,
  } = useLiveAgentSession();

  /*
   * 플러그인 목록 prefetch.
   *
   * ``connectWs.onopen`` 이 ``enabled_plugins`` 를 포함한 auth 를 보내기
   * 전에 HTTP ``listPlugins`` 결과를 받아두면, 핸드셰이크 critical path 에서
   * 네트워크 왕복이 사라져 첫 발화까지의 체감 지연이 줄어든다. 에러는 helper
   * 내부에서 swallow 되며 세션 자체에는 영향을 주지 않는다.
   */
  useEffect(() => {
    void prefetchLivePlugins();
  }, []);

  /* ------------------------------------------------------------------ */
  /* Scroll & anchor — `AgentContent.tsx` 의 정책을 그대로 이식.         */
  /* ------------------------------------------------------------------ */

  /** 컨테이너의 현재 위치가 "맨 아래에 가까운가" 를 ref 에 동기화. */
  const syncNearBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distance <= NEAR_BOTTOM_PX;
  }, []);

  /** 컨테이너를 맨 아래로 스크롤. behavior 기본값은 통합 에이전트와 동일하게
   *  smooth — 다만 라이브 에이전트에서는 외부에서 직접 호출하지 않고,
   *  conversation 전환 시 'auto' 로 한 번만 사용한다. */
  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      const el = containerRef.current;
      if (!el) return;
      lastProgrammaticScrollAtRef.current = nowMs();
      el.scrollTo({ top: el.scrollHeight, behavior });
      requestAnimationFrame(syncNearBottom);
    },
    [syncNearBottom],
  );

  /** 현재 앵커링 대상 user 버블을 viewport 상단(`USER_ANCHOR_TOP_PX`) 에
   *  맞춰 즉시 점프시킨다. `behavior: 'auto'` 라 사용자가 점프 자체를
   *  거의 인지하지 못할 만큼 빠르다.  찾지 못하면 false 반환 — 다음
   *  layout cycle 에서 재시도된다. */
  const alignAnchoredUser = useCallback(() => {
    const el = containerRef.current;
    const anchorMessageId = anchorMessageIdRef.current;
    if (!el || !anchorMessageId) return false;

    const anchorEl = Array.from(
      el.querySelectorAll<HTMLElement>('[data-message-id]'),
    ).find((node) => node.dataset.messageId === anchorMessageId);
    if (!anchorEl) return false;

    const nextTop =
      el.scrollTop
      + (anchorEl.getBoundingClientRect().top - el.getBoundingClientRect().top)
      - USER_ANCHOR_TOP_PX;

    lastProgrammaticScrollAtRef.current = nowMs();
    el.scrollTo({ top: Math.max(nextTop, 0), behavior: 'auto' });
    return true;
  }, []);

  /** 새 user 발화 *직전* 에 호출되어 앵커링을 요청한다.
   *
   *  통합 에이전트(`AgentContent`) 와 동일한 baseline 패턴 — emit 시점의
   *  `lastUserId`(=이전 latestUserId) 를 baseline 에 고정하고, 이후 store
   *  update 로 lastUserId 가 새 값으로 바뀌면 `useLayoutEffect` 가 promote
   *  → align 한다.  trigger 는 `agent:anchorLatestUser` 이벤트로 이루어지며,
   *  `useLiveAgentSession` 에서 `addMessage(...)` 직전에 emit 한다. */
  const requestUserAnchor = useCallback(() => {
    anchorActiveRef.current = true;
    pendingAnchorRequestRef.current = true;
    pendingAnchorBaselineUserIdRef.current = lastUserId;
    setIsAnchoringUser(true);
  }, [lastUserId]);

  /** 사용자가 wheel/touch/scroll 로 직접 스크롤하거나 conversation 이
   *  바뀌었을 때 호출. 앵커를 해제하고 spacer 도 제거한다. */
  const releaseAnchor = useCallback(() => {
    anchorActiveRef.current = false;
    pendingAnchorRequestRef.current = false;
    pendingAnchorBaselineUserIdRef.current = null;
    anchorMessageIdRef.current = null;
    setIsAnchoringUser(false);
  }, []);

  /** transcript streaming 동안 layout 이 늘어나는지 감지하기 위한
   *  signature — 메시지 개수 + 마지막 메시지 id + 마지막 메시지 텍스트
   *  길이 조합.  통합 에이전트(`content.length`) 와 동일한 정책이며,
   *  `LiveAgentMessage` 는 thinkingContent / streamEvents 가 없으므로
   *  `text.length` 만 결합한다. */
  const lastMessageForSignature = messages[messages.length - 1] ?? null;
  const anchorLayoutSignature = useMemo(
    () =>
      [
        messages.length,
        lastMessageForSignature?.id ?? 'empty',
        lastMessageForSignature?.text.length ?? 0,
      ].join(':'),
    [messages.length, lastMessageForSignature?.id, lastMessageForSignature?.text.length],
  );

  /** `useLayoutEffect` — paint 직전에 실행. `pending` 요청이 있으면
   *  promote 를 시도하고, anchor message 가 확정되면 align.  `AgentContent`
   *  와 동일한 흐름. */
  useLayoutEffect(() => {
    if (!isAnchoringUser) return;
    if (pendingAnchorRequestRef.current) {
      if (!lastUserId) return;
      if (lastUserId === pendingAnchorBaselineUserIdRef.current) return;
      anchorMessageIdRef.current = lastUserId;
      pendingAnchorRequestRef.current = false;
      pendingAnchorBaselineUserIdRef.current = null;
    }
    if (!anchorMessageIdRef.current) return;
    alignAnchoredUser();
    syncNearBottom();
  }, [
    alignAnchoredUser,
    anchorLayoutSignature,
    isAnchoringUser,
    lastUserId,
    syncNearBottom,
  ]);

  /** `ResizeObserver` 로 컨테이너 / inner / 자식들의 크기 변동(키보드
   *  노출, viewport resize, transcript 누적) 에도 동일한 align 을 적용. */
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    let rafId = 0;
    const scheduleRealign = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        if (!anchorActiveRef.current) return;
        alignAnchoredUser();
      });
    };

    const observer = new ResizeObserver(scheduleRealign);
    observer.observe(el);
    const inner = el.firstElementChild;
    if (inner instanceof Element) observer.observe(inner);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [alignAnchoredUser]);

  /** 사용자가 직접 스크롤(wheel/touch) 하면 앵커 해제. programmatic
   *  스크롤은 guard 로 무시. */
  const handleScroll = useCallback(() => {
    syncNearBottom();
    if (!anchorActiveRef.current) return;
    const elapsed = nowMs() - lastProgrammaticScrollAtRef.current;
    if (elapsed < PROGRAMMATIC_SCROLL_GUARD_MS) return;
    releaseAnchor();
  }, [releaseAnchor, syncNearBottom]);

  /** `agent:anchorLatestUser` 이벤트 listener.
   *
   *  통합 에이전트(`AgentContent`) 와 100% 동일한 트리거 메커니즘.
   *  `useLiveAgentSession` 이 새 user 메시지를 store 에 추가하기 직전에
   *  이 이벤트를 발행하므로, 별도 `lastUserId` 자동 트리거 effect 가
   *  필요 없다.  이전엔 `skipNextAnchorRef` 로 history 로딩과 발화를
   *  구분했으나, conversation 전환과 lastUserId 변경이 같은 commit 에서
   *  fire 될 때 race 가 생겨 후속 turn 트리거가 누락되는 버그가 있었다.
   *  이벤트 기반 트리거는 store 외부의 명시적 신호이므로 해당 race 가
   *  근본적으로 발생하지 않는다. */
  useEffect(() => {
    return onAppEvent('agent:anchorLatestUser', requestUserAnchor);
  }, [requestUserAnchor]);

  /** conversation 전환 시 앵커 해제. */
  useEffect(() => {
    releaseAnchor();
    isNearBottomRef.current = true;
  }, [activeId, releaseAnchor]);

  useLayoutEffect(() => {
    if (!activeId) return;
    if (initialScrollConversationRef.current === activeId) return;
    if (messages.length === 0) return;
    initialScrollConversationRef.current = activeId;
    scrollToBottom('auto');
  }, [activeId, messages.length, scrollToBottom]);

  const handleStart = useCallback(async () => {
    await startSession();
  }, [startSession]);

  const handleCopy = useCallback(async (id: string, text: string) => {
    const ok = await copyTextFallback(text);
    if (ok) {
      setCopiedIds((prev) => new Set(prev).add(id));
      setTimeout(() => setCopiedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      }), 2000);
    }
  }, []);

  const handleFeedback = useCallback((id: string, type: 'up' | 'down') => {
    setFeedbackMap((prev) => {
      const next = { ...prev };
      if (prev[id] === type) {
        delete next[id];
      } else {
        next[id] = type;
      }
      return next;
    });
    setShakingFeedback({ id, type });
    setTimeout(() => {
      setShakingFeedback((prev) =>
        prev && prev.id === id && prev.type === type ? null : prev,
      );
    }, 450);
  }, []);

  useEffect(() => {
    if (sessionStatus === 'disconnected' || sessionStatus === 'idle') {
      micActiveRef.current = false;
    }
  }, [sessionStatus]);

  /* Thinking indicator 의 mount delay. trigger 조건이 성립하면 250ms 후
   * `showThinkingIndicator = true`. 조건이 깨지면(=어시스턴트 메시지 도착,
   * 세션 종료 등) 즉시 false 로 떨어뜨려 자연스럽게 unmount.
   *
   * trigger 조건 :
   *   transcript UI 활성   (`transcriptionEnabled`)
   *   세션이 살아 있음     (`sessionStatus === 'active'`)
   *   마지막 메시지가 user (`lastAuthor === 'user'`)
   *
   * `messages.length` 가 아닌 마지막 메시지의 author 를 보므로, 어시스턴트
   * 메시지가 push 되면 동일한 effect 가 다시 돌아 indicator 를 즉시 끈다. */
  const lastAuthor = messages.length > 0 ? messages[messages.length - 1].author : null;
  useEffect(() => {
    const shouldShow =
      transcriptionEnabled
      && sessionStatus === 'active'
      && lastAuthor === 'user';
    if (!shouldShow) {
      setShowThinkingIndicator(false);
      return;
    }
    const timer = window.setTimeout(() => setShowThinkingIndicator(true), 250);
    return () => window.clearTimeout(timer);
  }, [transcriptionEnabled, sessionStatus, lastAuthor]);

  useEffect(() => {
    if (sessionStatus === 'active' && !micActiveRef.current) {
      startMicrophone().then(() => {
        micActiveRef.current = true;
      });
    }
  }, [sessionStatus, startMicrophone]);

  const isIdle = sessionStatus === 'idle' || sessionStatus === 'disconnected' || sessionStatus === 'error';

  const getAgentLabel = (author: string): string => {
    const key = `liveAgent.agents.${author}`;
    const translated = t(key);
    return translated === key ? author : translated;
  };

  if (isIdle && messages.length === 0) {
    return (
      <PageWrapper>
        <EmptyState
          titleSize="2.25rem"
          titlePrefix="Askyour"
          rotatingTitles={AGENT_BRAND_SUFFIXES}
          description={t('liveAgent.welcomeMessage')}
          descriptionTone="strong"
          action={
            <StartCard onClick={handleStart}>
              <StartIconWrap>
                <Radio size={22} />
              </StartIconWrap>
              {t('liveAgent.startSession')}
            </StartCard>
          }
        />
        <LiveAgentSettingsModal />
      </PageWrapper>
    );
  }

  if (!transcriptionEnabled) {
    /* P11-5i: audio-only voice mode — the live session still streams,
     * but the transcript bubble surface is suppressed. We surface the
     * brand hero in its "session active" form so users still get the
     * speaking-orb affordance via the SettingsModal / dock. */
    return (
      <PageWrapper>
        <EmptyState
          rotatingTitles={AGENT_BRAND_SUFFIXES}
          description={t('liveAgent.welcomeMessage')}
          descriptionTone="strong"
        />
        <LiveAgentSettingsModal />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <MessagesArea
        ref={containerRef}
        onScroll={handleScroll}
        onWheel={releaseAnchor}
        onTouchMove={releaseAnchor}
      >
        <MessagesInner>
          {messages.map((msg) => {
            const isUser = msg.author === 'user';
            const isCopied = copiedIds.has(msg.id);
            return (
              <Row key={msg.id} $isUser={isUser} data-message-id={msg.id}>
                {!isUser && (
                  <AvatarCol>
                    <AgentAvatarImg
                      src="/agent_logo.png"
                      alt={getAgentLabel(msg.author)}
                    />
                  </AvatarCol>
                )}
                {isUser ? (
                  <UserSection>
                    <UserBubble>{msg.text}</UserBubble>
                    {msg.text && (
                      <BubbleActions>
                        <ActionBtn onClick={() => handleCopy(msg.id, msg.text)} title={isCopied ? t('chat.copied') : t('tooltip.copy')}>
                          {isCopied ? <Check size={13} /> : <Copy size={13} />}
                        </ActionBtn>
                      </BubbleActions>
                    )}
                  </UserSection>
                ) : (
                  <BubbleRow>
                    <AssistantCard>
                      <AgentNameLabel>{getAgentLabel(msg.author)}</AgentNameLabel>
                      <AssistantContent>{msg.text}</AssistantContent>
                      {msg.text && (
                        <AssistantActions>
                          <Tooltip label={isCopied ? t('chat.copied') : t('tooltip.copy')}>
                            <CardActionBtn onClick={() => handleCopy(msg.id, msg.text)}>
                              {isCopied ? <Check size={14} /> : <Copy size={14} />}
                            </CardActionBtn>
                          </Tooltip>
                          <Tooltip label={t('tooltip.like')}>
                            <CardFeedbackBtn
                              $active={feedbackMap[msg.id] === 'up'}
                              className={
                                shakingFeedback?.id === msg.id && shakingFeedback.type === 'up'
                                  ? 'shaking'
                                  : ''
                              }
                              onClick={() => handleFeedback(msg.id, 'up')}
                            >
                              <ThumbsUp size={14} />
                            </CardFeedbackBtn>
                          </Tooltip>
                          <Tooltip label={t('tooltip.dislike')}>
                            <CardFeedbackBtn
                              $active={feedbackMap[msg.id] === 'down'}
                              className={
                                shakingFeedback?.id === msg.id && shakingFeedback.type === 'down'
                                  ? 'shaking'
                                  : ''
                              }
                              onClick={() => handleFeedback(msg.id, 'down')}
                            >
                              <ThumbsDown size={14} />
                            </CardFeedbackBtn>
                          </Tooltip>
                        </AssistantActions>
                      )}
                    </AssistantCard>
                    {/* 이 turn 동안 도구가 push 한 카드들. 답변 버블/액션
                        하단에 별도 영역으로 렌더해 버블의 max-width 를
                        절대 밀지 않는다.  카드 0개면 ``LiveAgentCardRail``
                        이 자체적으로 ``null`` 을 반환. */}
                    <LiveAgentCardRail cards={msg.cards} />
                  </BubbleRow>
                )}
              </Row>
            );
          })}
          {/* user 발화 ~ 어시스턴트 finished transcript 도착 사이의 공백을
              채우는 dot pulsing indicator. `delayedShow` 는 250ms 지연 후
              true 가 돼 짧은 응답에서 깜빡이지 않게 한다. */}
          {showThinkingIndicator && (
            <TypingIndicator ariaLabel={t('liveAgent.thinking', { defaultValue: 'Thinking…' })} />
          )}
          {/* `AgentContent` 와 동일하게, 앵커링 활성 시에만 70vh spacer 를
              두어 마지막 user 버블이 viewport 상단으로 올라갈 수 있는
              아래쪽 여유 공간을 확보한다. 메시지가 짧을 때 항상 60vh 를
              비워두던 이전 정책은 제거됐다. */}
          {isAnchoringUser && messages.length > 0 && (
            <div style={{ minHeight: USER_ANCHOR_BOTTOM_SPACER }} />
          )}
        </MessagesInner>
      </MessagesArea>
      <LiveAgentSettingsModal />
    </PageWrapper>
  );
};

export default LiveAgentPage;
